import { useEffect, useRef } from 'react';

type Callbacks = {
    readonly onScrollDown?: () => void;
    readonly onScrollUp?: () => void;
};

const readScrollPosition = (target: EventTarget | null): number => {
    if (target === document || target === window) {
        return window.scrollY;
    }

    if (target instanceof HTMLElement) {
        return target.scrollTop;
    }

    return 0;
};

/**
 * Listens for scroll events anywhere on the page (capture phase, since
 * `scroll` doesn't bubble) and invokes `onScrollDown` / `onScrollUp` based
 * on the per-target delta. The scroll target is tracked so deltas only
 * count when the same surface keeps being scrolled.
 */
export const useScrollDirection = ({ onScrollDown, onScrollUp }: Callbacks): void => {
    const trackerRef = useRef<{ target: EventTarget | null; position: number }>({
        target: null,
        position: 0,
    });

    useEffect(() => {
        const handleScroll = (event: Event): void => {
            const target = event.target;
            const position = readScrollPosition(target);
            const tracker = trackerRef.current;

            if (tracker.target === target) {
                const delta = position - tracker.position;

                if (delta > 0) {
                    onScrollDown?.();
                } else if (delta < 0) {
                    onScrollUp?.();
                }
            }

            trackerRef.current = { target, position };
        };

        window.addEventListener('scroll', handleScroll, { capture: true, passive: true });

        return () => window.removeEventListener('scroll', handleScroll, { capture: true });
    }, [onScrollDown, onScrollUp]);
};
